import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AccessibilityProvider } from './context/AccessibilityContext';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import LanguageTranslator from './components/LanguageTranslator';
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import TechnologyPage from './pages/TechnologyPage';
import FindMyPage from './pages/FindMyPage';
import AboutPage from './pages/AboutPage';
import ImpactPage from './pages/ImpactPage';
import PressPage from './pages/PressPage';
import PartnerPage from './pages/PartnerPage';
import ShopPage from './pages/ShopPage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import SafetyNoticePage from './pages/SafetyNoticePage';
import TrainingPage from './pages/TrainingPage';
import WarrantyPage from './pages/WarrantyPage';
import NotFoundPage from './pages/NotFoundPage';
import SEOManager from './components/SEOManager';
import CursorGlow from './components/CursorGlow';
import './App.css';

// Transactional, admin, blog, and diagnostic routes are lazy-loaded so their
// dependencies (Razorpay/Maps in checkout, html5-qrcode in device-check — the
// single largest item in the bundle — Sanity/portable-text in blog) don't ship
// to every marketing-page visitor.
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ConfiguratorPage = lazy(() => import('./pages/ConfiguratorPage'));
const PayPage = lazy(() => import('./pages/PayPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage'));
const OrderFailedPage = lazy(() => import('./pages/OrderFailedPage'));
const TrackOrderPage = lazy(() => import('./pages/TrackOrderPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ConfigureOrderPage = lazy(() => import('./pages/ConfigureOrderPage'));
const DeviceCheckPage = lazy(() => import('./pages/DeviceCheckPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminDevicesPage = lazy(() => import('./pages/AdminDevicesPage'));
const AdminDeviceDetailPage = lazy(() => import('./pages/AdminDeviceDetailPage'));

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// The Configure & Request flow and the customer account area each ship their
// own full-bleed chrome (Claude Design handoffs "ClearWay Configurator v3" and
// "ClearWay Account"), so the site header and footer are suppressed there
// rather than stacked on top of them. The account matches on prefix — every
// screen under /account carries the same sidebar.
const CHROMELESS_ROUTES = ['/configure'];
const CHROMELESS_PREFIXES = ['/account'];

const SiteChrome = ({ children }) => {
  const { pathname } = useLocation();
  const path = pathname.replace(/\/$/, '') || '/';
  const bare = CHROMELESS_ROUTES.includes(path)
    || CHROMELESS_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  return (
    <>
      {!bare && <Navigation />}
      {children}
      {!bare && <Footer />}
    </>
  );
};

const RouteFallback = () => (
  <div className="min-h-screen pt-32 pb-20 px-6" role="status" aria-live="polite">
    <div className="max-w-7xl mx-auto">
      <div className="skeleton h-96 rounded-3xl" />
      <span className="sr-only">Loading…</span>
    </div>
  </div>
);

function App() {
  return (
    <AccessibilityProvider>
      <Router>
        <div className="min-h-screen bg-[#0a0a0a] text-white">
          <CursorGlow />
          <ScrollToTop />
          <SEOManager />
          <LanguageTranslator />
          <SiteChrome>
            <main id="main-content" tabIndex="-1">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/technology" element={<TechnologyPage />} />
                  <Route path="/find-my" element={<FindMyPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/impact" element={<ImpactPage />} />
                  <Route path="/press" element={<PressPage />} />
                  <Route path="/partner" element={<PartnerPage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/configure" element={<ConfiguratorPage />} />
                  <Route path="/pay/:orderNumber" element={<PayPage />} />
                  <Route path="/order/success" element={<OrderSuccessPage />} />
                  <Route path="/order/failed" element={<OrderFailedPage />} />
                  <Route path="/track" element={<TrackOrderPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/refund" element={<RefundPage />} />
                  <Route path="/safety-notice" element={<SafetyNoticePage />} />
                  <Route path="/training" element={<TrainingPage />} />
                  <Route path="/warranty" element={<WarrantyPage />} />
                  <Route path="/support" element={<WarrantyPage />} />
                  <Route path="/support/device-check" element={<DeviceCheckPage />} />
                  <Route path="/configure/:orderNumber" element={<ConfigureOrderPage />} />
                  <Route path="/account/*" element={<AccountPage />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/devices" element={<AdminDevicesPage />} />
                  <Route path="/admin/devices/:serialNumber" element={<AdminDeviceDetailPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </main>
          </SiteChrome>
        </div>
      </Router>
    </AccessibilityProvider>
  );
}

export default App;
